import http, { handleHttpError, handleHttpResponse } from 'services/http'
import { HTTPResponse, IPaginationResponse } from 'services/http/http.types'
import {
  IAddExpertToRoomDto,
  IAddTeamToRoomDto,
  IAddTeamToRoomResponse,
  ICreateDefenceDto,
  ICreateRoomDto,
  ICreateRoomResponse,
  IDeleteRoomResponse,
  IExpertsResponse,
  IGrade,
  IMeRegistrations,
  IMoveTeamToRoomDto,
  IProtectionDay,
  IProtectionDayByIdResponse,
  IProtectionDayResponse,
  IRoom,
  ITeam,
  ITeamArtefactsDto,
  ITeamArtefactsResponse,
  ITeamDefence,
  ITeamResult,
  ITeamReviewFormResponse,
  ITeamSubmitReviewDto,
  ITeamSubmitReviewResponse,
  IUpdateDefenceDto,
  IUpdateRoomDto,
} from './protection.types'

interface IGetParams {
  search?: string
  roomIds?: string
  defenceId?: string
  expertIds?: string[]
  limit?: string
  from?: string
  to?: string
}
const listGrade = async (params: IGetParams): Promise<HTTPResponse<IPaginationResponse<IGrade>>> => {
  return http.get('/nsi/v1/reviews', { params }).then(handleHttpResponse).catch(handleHttpError)
}

const listProtectionDays = async (params: IGetParams): Promise<HTTPResponse<IPaginationResponse<IProtectionDay>>> => {
  return http.get('/nsi/v1/defences', { params }).then(handleHttpResponse).catch(handleHttpError)
}

const getDefenceById = async (
  defenceId: IProtectionDayResponse['id']
): Promise<HTTPResponse<IProtectionDayByIdResponse>> => {
  return http.get(`/nsi/v1/defences/${defenceId}`).then(handleHttpResponse).catch(handleHttpError)
}

const createDefence = async (dto: ICreateDefenceDto): Promise<HTTPResponse<IProtectionDayResponse>> => {
  return http.post('/nsi/v1/defences', dto).then(handleHttpResponse).catch(handleHttpError)
}

const deleteDefence = async (
  defenceId: IProtectionDayResponse['id']
): Promise<HTTPResponse<IProtectionDayResponse>> => {
  return http.delete(`/nsi/v1/defences/${defenceId}`).then(handleHttpResponse).catch(handleHttpError)
}

const updateDefence = async (
  defenceId: IUpdateDefenceDto['id'],
  dto: Omit<IUpdateDefenceDto, 'id'>
): Promise<HTTPResponse<IProtectionDayResponse>> => {
  return http.put(`/nsi/v1/defences/${defenceId}`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const createRoom = async (dto: ICreateRoomDto): Promise<HTTPResponse<ICreateRoomResponse>> => {
  return http.post('/nsi/v1/rooms', dto).then(handleHttpResponse).catch(handleHttpError)
}

const updateRoom = async (
  createRoomId: IRoom['id'],
  dto: IUpdateRoomDto
): Promise<HTTPResponse<ICreateRoomResponse>> => {
  return http.put(`/nsi/v1/rooms/${createRoomId}`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const deleteRoom = async (deleteRoomId: IRoom['id']): Promise<HTTPResponse<IDeleteRoomResponse>> => {
  return http.delete(`/nsi/v1/rooms/${deleteRoomId}`).then(handleHttpResponse).catch(handleHttpError)
}

const addExpertToRoom = async (
  roomId: IRoom['id'],
  dto: IAddExpertToRoomDto
): Promise<HTTPResponse<IProtectionDayResponse>> => {
  return http.post(`/nsi/v1/rooms/${roomId}/experts`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const addTeamToRoom = async (
  roomId: IRoom['id'],
  dto: IAddTeamToRoomDto
): Promise<HTTPResponse<IAddTeamToRoomResponse>> => {
  return http.post(`/nsi/v1/rooms/${roomId}/teams`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const deleteTeamFromRoom = async (
  roomId: IRoom['id'],
  teamId: ITeam['id']
): Promise<HTTPResponse<IAddTeamToRoomResponse>> => {
  return http.delete(`/nsi/v1/rooms/${roomId}/teams/${teamId}`).then(handleHttpResponse).catch(handleHttpError)
}
const moveTeamToRoom = async (
  roomId: IRoom['id'],
  teamId: ITeam['id'],
  dto: IMoveTeamToRoomDto
): Promise<HTTPResponse<IMoveTeamToRoomDto>> => {
  return http
    .patch(`/nsi/v1/rooms/${roomId}/teams/${teamId}/order`, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getStudentRegistrations = async (
  params: IGetParams
): Promise<HTTPResponse<IPaginationResponse<IMeRegistrations>>> => {
  return http.get('/nsi/v1/students/me/registrations', { params }).then(handleHttpResponse).catch(handleHttpError)
}

const getTeamResult = async (
  teamId: ITeam['id'],
  defenceId: IProtectionDay['id']
): Promise<HTTPResponse<ITeamResult>> => {
  return http.get(`/nsi/v1/defence/${defenceId}/team/${teamId}/result`).then(handleHttpResponse).catch(handleHttpError)
}

const getMyTeamResult = async (params: IGetParams): Promise<HTTPResponse<IPaginationResponse<ITeamDefence>>> => {
  return http.get(`/nsi/v1/experts/me/teams`, { params }).then(handleHttpResponse).catch(handleHttpError)
}

const getTeamReviewForm = async (
  teamId: ITeamDefence['teamId'],
  defenceId: IProtectionDay['id']
): Promise<HTTPResponse<ITeamReviewFormResponse>> => {
  return http.get(`/nsi/v1/teams/${teamId}/${defenceId}/review-form`).then(handleHttpResponse).catch(handleHttpError)
}

const submitTeamReviewForm = async (dto: ITeamSubmitReviewDto): Promise<HTTPResponse<ITeamSubmitReviewResponse>> => {
  return http.post(`/nsi/v1/reviews`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const getExperts = async (params: IGetParams): Promise<HTTPResponse<IPaginationResponse<IExpertsResponse>>> => {
  return http.get(`/nsi/v1/users/experts`, { params }).then(handleHttpResponse).catch(handleHttpError)
}

const deleteReview = async (id: IGrade['id']): Promise<HTTPResponse<IPaginationResponse<any>>> => {
  return http.delete(`/nsi/v1/reviews/${id}`).then(handleHttpResponse).catch(handleHttpError)
}

const addArtefacts = async (dto: ITeamArtefactsDto): Promise<HTTPResponse<ITeamArtefactsResponse>> => {
  return http.post(`/nsi/v1/team-artefacts`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const protectionsApi = {
  listGrade,
  listProtectionDays,
  createDefence,
  updateDefence,
  getDefenceById,
  createRoom,
  addExpertToRoom,
  addTeamToRoom,
  deleteTeamFromRoom,
  moveTeamToRoom,
  getStudentRegistrations,
  getTeamResult,
  updateRoom,
  getMyTeamResult,
  deleteRoom,
  getTeamReviewForm,
  submitTeamReviewForm,
  deleteDefence,
  getExperts,
  deleteReview,
  addArtefacts,
}
export default protectionsApi
